import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Activity, User, Search, Menu, LogOut, LogIn, ChevronDown, Star } from "lucide-react";
import { supabase } from "@/integrations/db/client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import UserAvatar from "@/components/UserAvatar";
import PrimaryNav from "@/components/PrimaryNav";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTenant, useSwitchableTenants, useSwitchTenant, useSetPreferredTenant } from "@/hooks/useTenant";
import TenantWordmark, { BrandMark } from "@/components/shared/TenantWordmark";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useUserRole } from "@/hooks/useUserRole";

/**
 * One tenant's mark, at menu-row size, for a row of the workspace switcher.
 *
 * A workspace is recognised by its logo long before its name is read, so the
 * list shows the same identity the brand zone is about to wear — resolved the
 * same way, through `BrandMark`: the tenant's stored logo, with its dark cut
 * swapped in by `dark:` wherever that artwork exists.
 *
 * Two cases the header never has to answer, because it only ever draws the
 * marks the app ships:
 *
 * - A logo with NO dark cut (XGB, EHP) is chromatic art that reads fine on
 *   either ground, so it renders straight onto the dark menu surface with no
 *   background treatment of its own — the slot's background is fully
 *   transparent in both themes, this pair included. A mark that HAS a dark
 *   cut needs nothing special either: the cut is already the answer.
 * - A tenant with no logo at all (the unconfigured default) falls back to its
 *   initial on the avatar plate, which is what a person without a picture
 *   gets — that small round plate is the app's standard no-picture fallback,
 *   not a background treatment of the logo slot, so none of the above touches
 *   it.
 *
 * The slot is one FIXED 64×24 box whichever branch a tenant takes, and the
 * artwork is bound by its WIDTH inside it — `w-full h-auto`, with the 24px
 * height only a cap. A shared slot is not the same thing as a shared mark
 * size: the old slot drew every logo 18px TALL, and a mark 18px tall spends
 * whatever width its ratio asks for (Everon ~73px, EHP ~54px, XGB ~50px), so
 * the marks sat in a tidy column at three visibly different sizes. Drawn to
 * the slot's width instead, every mark is the same width and the column reads
 * as one set.
 *
 * 64 is chosen so width is the BINDING constraint for all of them: an image
 * fills the slot's width and stays under the cap whenever its ratio is at
 * least 64/24 = 2.67, and the narrowest mark the app ships is XGB's 213×76 =
 * 2.80 (the rest run to Everon's 4.04). Wide-and-short marks therefore render
 * shorter than the cap — Everon at 64×15.8 against XGB's 64×22.8 — which is
 * what equal width means for artwork of different proportions, and is the
 * trade taken deliberately. `max-h-6` + `object-contain` is the backstop for
 * artwork we have not seen: a tenant may upload something squarer than 2.67,
 * and that letterboxes inside the slot rather than growing the row.
 */
const MARK_SLOT = "h-6 w-16 shrink-0 items-center justify-center";

const TenantSwitcherMark = ({ logo, name }: { logo: string | null; name: string }) => {
  if (!logo) {
    return (
      <span className={cn("flex", MARK_SLOT)}>
        <span className="avatar-plate flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold text-primary dark:text-blue-200">
          {name.trim().charAt(0).toUpperCase() || "?"}
        </span>
      </span>
    );
  }
  return (
    <span className={cn("flex", MARK_SLOT)}>
      <BrandMark src={logo} alt="" className="h-auto max-h-6 w-full object-contain" />
    </span>
  );
};

const TopNav = () => {
  const { id: tenantId, name: tenantName, logo: tenantLogo } = useTenant();
  const switchableTenants = useSwitchableTenants();
  const switchTenant = useSwitchTenant();
  const setPreferred = useSetPreferredTenant();
  const navigate = useNavigate();

  const { data: userProfile, userId } = useUserProfile();
  const { data: userRole } = useUserRole();

  const [mobileOpen, setMobileOpen] = useState(false);

  const isAuthenticated = !!userId;

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) navigate("/login");
  };

  const isStaff = userRole === "superadmin" || userRole === "admin" || userRole === "member";

  return (
    <header className="relative flex-shrink-0 border-b border-border bg-card">
      {/* Phone widths tighten the bar's own padding and gaps. Every pixel here
          is a pixel the brand lockup does not have to give up — the right
          cluster used to be pushed clean off the header on a 375px screen,
          which took the avatar menu (and with it Activity, Profile and Log
          out) off the app entirely on a phone. */}
      <div className="relative flex h-14 items-center gap-3 px-4 max-sm:gap-2 max-sm:px-3 sm:px-5">
        {/* The Sheet root wraps the brand zone so its trigger can live INSIDE
            the zone (Radix context), with the drawer content declared after. */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          {/* logo + wordmark — a tenant switcher when the user belongs to >1.
            Wrapped in `.brand-zone` (index.css): the marks are supplied
            artwork, so they cannot be recoloured per theme the way the rest of
            the chrome is, and on the dark header the XBG mark was ink-on-ink.
            The zone gives them a slightly lighter ground to sit on and closes
            with a rule, which also does the job the gap was failing to do —
            saying where the identity ends and the app begins. On `lg` it
            bleeds to the window edge, so it reads as a corner of the chrome
            rather than a chip floating in it.

            At `lg` it is also EXACTLY the sidebar's width (w-60, 240px), so
            that closing rule continues the sidebar's right edge straight up
            through the header and the two read as one corner rather than as
            two panels that nearly line up. That makes 240 a width to HIT, so
            the lockup is fitted to the zone (`dense` marks, a 15px label)
            rather than the zone to the lockup: 214px of content inside it with
            the switcher's chevron, which leaves "Everon Backlog" whole. Below
            `lg` there is no sidebar to line up with and the zone goes back to
            taking the width its contents need.

            On a phone the umbrella mark is the part that goes: the tenant's own
            logo is the identity, "XBG |" is the co-brand above it, and the
            header has room for one of the two beside a hamburger, a search, a
            theme toggle and an avatar. `min-w-0` is the backstop — a long
            tenant name shrinks the zone rather than shouldering the right
            cluster off the screen. */}
          {/* Below `lg` the hamburger lives INSIDE the zone: it opens the same
            navy identity plane the zone fronts for, and with it outside, the
            zone's dark-mode tint started mid-header and read as chopped. The
            negative margins bleed the tint to the window edge at every width,
            mirroring what `lg:-ml-5` always did for the desktop corner. */}
          {/* The bleed margins mirror the header's own padding tiers exactly
              (px-3 below sm, px-5 from sm) — one tier off and a strip of
              header shows at the window edge. */}
          <div className="brand-zone -ml-3 flex h-14 min-w-0 shrink items-center gap-1 self-center pl-2 pr-3 max-sm:pr-2 max-sm:[&_.brand-divider]:hidden max-sm:[&_.brand-parent-mark]:hidden sm:-ml-5 sm:pl-4 lg:w-60 lg:shrink-0 lg:gap-0 lg:pr-2">
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="shrink-0 lg:hidden" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            {switchableTenants.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  disabled={switchTenant.isPending}
                  className="flex h-5 min-w-0 items-center gap-1.5 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                >
                  <TenantWordmark
                    logo={tenantLogo}
                    name={tenantName}
                    tenantId={tenantId}
                    dense
                    className="text-[15px] font-bold tracking-tight text-foreground"
                  />
                  {/* Rides the label's baseline correction: the chevron belongs to
                  the wordmark it opens, so it moves with the text rather than
                  staying on the row's geometric centre. */}
                  {/* No baseline nudge in the dense lockup — the label dropped
                    its own (see TenantWordmark), and the chevron travels with
                    the label. */}
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </DropdownMenuTrigger>
                {/* The menu is the zone continued downward: the zone's width to
                  the pixel, and `alignOffset` gives back the zone's own left
                  padding so the two share an edge. At `lg` that makes the open
                  menu the sidebar's column exactly — 0 to 240 — so it reads as
                  the identity block extending rather than as a popover that
                  happens to be near it.

                  It has to CLEAR the zone to read that way, and the default
                  offset does not. Radix measures from the trigger, which is
                  the 20px wordmark row centred in a 56px header — so the
                  standard 4px of air put the menu's top edge 14px above the
                  header's bottom rule, with the open panel lying across the
                  bottom of the brand zone it is supposed to continue. The
                  offset is that gap paid off: 18px from the trigger's bottom
                  edge to the header's, plus the same 4px of air every other
                  menu in the app leaves. */}
                {/* 19 puts the menu's top edge at the header's bottom rule
                  (trigger bottom 38 + 19 = 57): flush with the brand zone, no
                  sliver of nav visible between the lockup and the menu. */}
                <DropdownMenuContent side="bottom" sideOffset={19} align="start" alignOffset={-16} className="w-60">
                  <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* Each row is two columns with a rule between them: the mark
                      on a fixed-width slot at the left, the name pushed to the
                      right edge. The marks share a left edge, the names share a
                      right one, and because the slot never varies the rule lands
                      at the same x on every row — a column of logos and a column
                      of names rather than one ragged left-packed line.

                      The rule is the brand lockup's own divider at menu scale:
                      the same `w-px` of `--muted-foreground` the header draws
                      between the XBG mark and the tenant wordmark, one step
                      fainter (40% → 25%) because it is separating two parts of a
                      list row rather than two halves of a lockup, and it repeats
                      on every row. Full content height (h-6, the slot's), so it
                      reads as the column edge it is.

                      The CURRENT workspace gets a faint standing box rather
                      than a rail: a quiet tint plus a matching hairline ring,
                      both in the menu's own accent hue at reduced strength
                      (`bg-accent/10`, `ring-accent/20`) — the same "this row
                      is special" language Radix's own keyboard/hover
                      highlight already speaks on every item
                      (`focus:bg-focus/10` from the base item), held at rest
                      rather than only on interaction. Same treatment as the
                      current row in PropertyControls' option list.

                      The highlight used to be a solid `--accent` fill. It sank
                      every logo, the divider and the star on the hovered row
                      into navy (dark-ink marks at ~1.8:1), so the base items
                      now take Select's light tint instead.

                      The tint is scoped to `:not(:focus)` so the two never
                      fight over the same `background-color`: the moment
                      Radix focuses/highlights the row, the standing tint's
                      selector stops matching and the highlight fill wins
                      outright — no specificity gamble to reason about. On
                      this row that fill is taken a step up (`bg-focus/20`),
                      because in light mode `--accent` and `--focus-border`
                      are the same colour and a 10% fill landing on a 10%
                      tint would be no change at all. The ring is a
                      box-shadow, a different property, so it keeps showing
                      right through the focus fill — the row still reads as
                      "the current one" while highlighted, not just at rest.
                      Plus a semibold name; no leading Check, which would
                      cost the marks their shared left edge, and no TRAILING
                      one, which would cost the names their shared right
                      edge — the whole point of the layout.

                      `disabled` is kept on the current row (it is not a place to
                      navigate to), but its opacity is not: the box and the
                      semibold ARE the indicator, and dimming the one row that
                      has to stand out is backwards. While a switch is in flight
                      every row dims, current included, because then the fade is
                      saying "busy", not "selected". */}
                  {/* The current row is no longer `disabled`. It used to be —
                      it is not a place to navigate to — but the base item
                      carries `data-[disabled]:pointer-events-none`, which would
                      have killed the star on the one row most people want to
                      star: the workspace they are sitting in. The click guard
                      below already makes the row inert, and the box, the
                      semibold name and aria-current are what mark it. Only a
                      switch in flight disables rows now, which is also when the
                      star should stop taking clicks. */}
                  {switchableTenants.map((t) => (
                    <DropdownMenuItem
                      key={t.id}
                      disabled={switchTenant.isPending}
                      data-current={t.current || undefined}
                      aria-current={t.current ? "true" : undefined}
                      onClick={() => {
                        if (!t.current) switchTenant.mutate(t);
                      }}
                      className={cn(
                        "group/row gap-2",
                        t.current && "ring-1 ring-inset ring-accent/20 [&:not(:focus)]:bg-accent/10 focus:bg-focus/20",
                        t.current && !switchTenant.isPending && "data-[disabled]:opacity-100",
                      )}
                    >
                      <TenantSwitcherMark logo={t.logo} name={t.name} />
                      <span aria-hidden="true" className="h-6 w-px shrink-0 bg-muted-foreground/25" />
                      {/* The mark carries the identity and takes its width
                          first; the name gives way to it rather than the menu
                          widening past the brand zone it continues. It fills
                          what is left and sets right, so a name that fits ends
                          on the shared right edge and one that does not starts
                          at the left of its own space and ellipses — the
                          readable end of a workspace name is its beginning. */}
                      <span className={cn("min-w-0 flex-1 truncate text-right", t.current && "font-semibold")}>
                        {t.name}
                      </span>
                      {/* The star says "start me here", not "take me here" —
                          a different verb on the same row, so it stops the
                          click rather than also switching. Same affordance as
                          the primary-assignee star on a ticket: filled when
                          set, otherwise revealed by hover AND by the keyboard
                          highlight, since a control that only exists under a
                          mouse pointer is not a control everyone has.

                          Coloured `--ring-strong`, not `--ring`: the star sits
                          on the row's tint whenever it is visible, and plain
                          `--ring` cyan lands at ~2.5:1 there in light mode
                          (2.1:1 on the current row's stronger fill), under the
                          3:1 an icon needs. The unset star is full-strength
                          muted for the same reason (/70 was ~2.5:1). */}
                      <span
                        role="button"
                        tabIndex={-1}
                        aria-pressed={t.preferred}
                        aria-label={
                          t.preferred
                            ? `${t.name} is your default workspace, click to clear`
                            : `Make ${t.name} your default workspace`
                        }
                        title={t.preferred ? "Your default workspace" : "Make default"}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!setPreferred.isPending) setPreferred.mutate(t.preferred ? null : t.id);
                        }}
                        className={cn(
                          "ml-1 shrink-0 rounded p-0.5 transition-all",
                          t.preferred
                            ? "text-[hsl(var(--ring-strong))]"
                            : "text-muted-foreground opacity-0 hover:scale-110 hover:text-[hsl(var(--ring-strong))] group-hover/row:opacity-100 group-focus/row:opacity-100",
                        )}
                      >
                        <Star className={cn("h-3.5 w-3.5", t.preferred && "fill-current")} />
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <NavLink to="/" className="flex h-5 min-w-0 items-center">
                <TenantWordmark
                  logo={tenantLogo}
                  name={tenantName}
                  tenantId={tenantId}
                  dense
                  className="text-[15px] font-bold tracking-tight text-foreground"
                />
              </NavLink>
            )}
          </div>

          {/* The sidebar, in a drawer: same navy plane, same entries, same
            cascade. `p-0` because PrimaryNav brings its own padding, and the
            gradient because the sheet's default is the page background. */}
          <SheetContent
            side="left"
            className="flex w-72 flex-col bg-gradient-to-b from-sidebar to-[hsl(var(--sidebar-background-deep))] p-0 text-sidebar-foreground"
          >
            <div className="flex h-14 shrink-0 items-center border-b border-white/10 px-4">
              {/* `onDark`: the drawer's plane is the sidebar's navy in BOTH
                themes, so the marks take their dark cuts outright rather
                than following the app's theme onto ink-on-ink. */}
              <TenantWordmark
                logo={tenantLogo}
                name={tenantName}
                tenantId={tenantId}
                onDark
                className="text-lg font-bold tracking-tight text-white"
              />
            </div>
            <PrimaryNav variant="sheet" onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* right cluster */}
        <div className="ml-auto flex shrink-0 items-center gap-2 max-sm:gap-1">
          {/* monday-style search field: a button dressed as an input, so the
              whole affordance opens the command palette without owning focus */}
          <button
            type="button"
            aria-label="Search"
            title="Search (⌘K)"
            onClick={() => window.dispatchEvent(new Event("open-command-palette"))}
            className="hidden h-8 w-56 items-center gap-2 rounded-md border border-border bg-card px-2.5 text-[13px] text-muted-foreground transition-colors hover:border-muted-foreground/40 sm:flex"
          >
            <Search className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 text-left">Search</span>
            <kbd className="rounded border border-border bg-muted px-1 text-[10px] font-medium">⌘K</kbd>
          </button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Search"
            title="Search (⌘K)"
            onClick={() => window.dispatchEvent(new Event("open-command-palette"))}
            className="h-9 w-9 rounded-full border border-border bg-card text-muted-foreground sm:hidden"
          >
            <Search className="h-4 w-4" />
          </Button>

          <ThemeToggle variant="icon" />

          {isAuthenticated && userProfile ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="relative rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <UserAvatar
                  avatarUrl={userProfile.avatar_url}
                  firstName={userProfile.first_name}
                  lastName={userProfile.last_name}
                  email={userProfile.email}
                  className="h-8 w-8 border-2 border-primary/20"
                  fallbackClassName="text-xs"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="truncate text-sm font-medium text-foreground">
                    {userProfile.first_name || userProfile.email}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{userProfile.email}</p>
                </div>
                <DropdownMenuSeparator />
                {/* Personal items only. Catch Up moved out of here onto the
                    Home dashboard as a panel — notifications shouldn't hide
                    behind an avatar click.

                    Activity came the other way, from a bare icon button in the
                    right cluster. Three unlabelled circles in a row — search,
                    activity, theme — and only the pulse glyph had to be
                    guessed at; it is a read-only feed nobody opens on a
                    schedule, so it costs nothing to put it behind the avatar
                    where it gets a word next to it. */}
                {isStaff && (
                  <DropdownMenuItem onClick={() => navigate("/activity")}>
                    <Activity className="mr-2 h-4 w-4" />
                    Activity
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  // A TINT on focus, not a solid red bar across the menu — the same
                  // treatment the backlog row's Delete item wears, so the app has
                  // one destructive-menu-item look rather than two.
                  className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => navigate("/login")} className="gap-2">
              <LogIn className="h-4 w-4" />
              Log in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopNav;
